import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Check,
  X,
  Star,
  Clock,
  AlertCircle,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';
import { ToastAction } from 'src/components/ui/toast';
import { trackEvent } from 'src/utils/analytics';
import { useSkillMatch, useUpdateSkillMatch } from 'src/hooks/useSeniorFlow';
import {
  SK_RECENCY,
  SK_STAR_TO_LEVEL,
  SK_LEVEL_TO_STAR,
  skMonthsHuman,
  skProfLabel,
  skInferRecency,
} from 'src/pages/MySkills/skillHelpers';

const monthsValid = (m) => {
  if (String(m).trim() === '') return false;
  const n = Number(m);
  return !Number.isNaN(n) && n >= 0;
};

// Seed each requirement row (shape: GET /candidates/my-pipelines/{id}/skill-match)
// with the local, editable fields the UI needs on top of whatever the API
// sent — candidate_* fields pre-fill when already set. Recency isn't sent
// back as a band by the API, only a currently-used flag + last-used date, so
// it's inferred the same way MySkills does; that inference can come back
// null (no last-used date and not currently in use), so a pre-filled row is
// only treated as already confirmed once all three required fields actually
// resolved — otherwise it opens straight into the panel so the candidate can
// fill in what's missing, same as any other "Yes".
const seedRow = (s) => {
  const hasSkill = !!s.candidate_has_skill;
  const proficiency = s.candidate_proficiency || null;
  const months =
    s.candidate_experience_months != null
      ? String(s.candidate_experience_months)
      : '';
  const recencyBand = skInferRecency({
    is_currently_used: s.candidate_is_currently_used,
    last_used_date: s.candidate_last_used_date,
  });
  const prefillComplete =
    hasSkill && !!proficiency && monthsValid(months) && !!recencyBand;
  return {
    ...s,
    answer: hasSkill ? 'yes' : null,
    confirmed: prefillComplete,
    striking: false,
    expanded: false,
    proficiency,
    months,
    recencyBand,
    error: false,
  };
};

// How long the row dims in place before it actually moves down into the
// "Skills you don't have" pile.
const STRIKE_MOVE_DELAY_MS = 420;

// Mobile-only swipe-to-reject: how far left (px) a row has to travel before
// release commits it as "No"; SWIPE_MAX caps how far the drag visually goes.
const SWIPE_COMMIT_PX = 88;
const SWIPE_MAX_PX = 120;

const rowMissing = (s) => {
  if (s.answer !== 'yes') return [];
  const miss = [];
  if (!s.proficiency) miss.push('proficiency');
  if (!monthsValid(s.months)) miss.push('experience');
  if (!s.recencyBand) miss.push('recency');
  return miss;
};

// skill-match's recency enum is CURRENT/WITHIN_1Y/WITHIN_3Y/OVER_3Y — the
// local recency band (shared with MySkills, whose own API takes ONE_TO_3Y
// for the same middle value) needs that one value translated on the way out.
const RECENCY_TO_API = { ONE_TO_3Y: 'WITHIN_3Y' };
const recencyForApi = (band) => RECENCY_TO_API[band] || band;

// PATCH /skill-match only wants the skills the candidate confirmed having —
// keyed by name/type (not requirement_skill_id), body: { skills: [...] }.
const toSkillMatchPayload = (skills) => ({
  skills: skills
    .filter((s) => s.answer === 'yes')
    .map((s) => ({
      name: s.name,
      type: s.type,
      proficiency: s.proficiency,
      experience_months: Math.round(Number(s.months)),
      recency: recencyForApi(s.recencyBand),
    })),
});

// Compact summary chips for a confirmed, collapsed row — same three segments
// (stars · duration · recency) SkillCard.jsx shows on its confirmed rowlet.
function buildRowMeta(s) {
  const months = Number(s.months) || 0;
  const starVal = SK_LEVEL_TO_STAR[s.proficiency] || 0;
  const recencyLabel = SK_RECENCY.find((r) => r.key === s.recencyBand)?.label;
  const segs = [];
  if (starVal > 0) {
    segs.push(
      <span
        className="slf-skill-rl-seg"
        key="prof"
        title={skProfLabel(s.proficiency)}
      >
        {[1, 2, 3, 4].map((i) => (
          <Star
            key={i}
            aria-hidden="true"
            className={i <= starVal ? 'is-on' : ''}
          />
        ))}
      </span>
    );
  }
  if (months > 0) {
    segs.push(
      <span className="slf-skill-rl-seg" key="exp">
        <Clock aria-hidden="true" />
        {skMonthsHuman(months)}
      </span>
    );
  }
  if (recencyLabel) {
    segs.push(<span key="rec">{recencyLabel}</span>);
  }
  return segs;
}

export function SkillsStage({
  scope,
  landingSecMappingId,
  firstName,
  companyName,
  onSubmitted,
  onBack,
}) {
  const { toast } = useToast();
  const hasScope = !!(scope?.token || scope?.secMappingId);
  // On a magic link, prefer the sec_mapping_id the landing call already
  // returned over decoding it back out of the token.
  const skillMatchScope = useMemo(
    () => ({ ...scope, landingSecMappingId }),
    [scope, landingSecMappingId]
  );
  const {
    data: skillMatchData,
    isLoading,
    isError,
    refetch,
  } = useSkillMatch(skillMatchScope);
  const updateSkillMatch = useUpdateSkillMatch(skillMatchScope);

  const [skills, setSkills] = useState([]);
  useEffect(() => {
    if (skillMatchData?.required_skills) {
      setSkills(skillMatchData.required_skills.map(seedRow));
    }
  }, [skillMatchData]);

  // Below the mobile shell breakpoint, the Yes/No pills give way to
  // swipe-left-for-No + tap-to-expand-for-Yes (see MySkills.jsx for the same
  // matchMedia pattern).
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(max-width: 767px)').matches
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Picking "No" dims the row in place first, then — once that's had time
  // to register — moves it down into the "Skills you don't have" pile.
  // `noTimers` holds the pending move per row so switching back to "Yes"
  // mid-transition can cancel it.
  const noTimers = useRef({});
  useEffect(
    () => () => {
      Object.values(noTimers.current).forEach(window.clearTimeout);
    },
    []
  );

  // `error: false` is the default so any ordinary field edit clears a
  // previous validation flag — but it must come before `...patch` so a call
  // that deliberately sets `error: true` (a failed Save) isn't clobbered.
  const setRow = (id, patch) =>
    setSkills((prev) =>
      prev.map((s) =>
        s.requirement_skill_id === id ? { ...s, error: false, ...patch } : s
      )
    );

  const setAnswer = (id, answer) => {
    if (noTimers.current[id]) {
      window.clearTimeout(noTimers.current[id]);
      delete noTimers.current[id];
    }

    if (answer === 'no') {
      setRow(id, { striking: true, expanded: false });
      noTimers.current[id] = window.setTimeout(() => {
        delete noTimers.current[id];
        setRow(id, {
          answer: 'no',
          striking: false,
          confirmed: false,
          proficiency: null,
          months: '',
          recencyBand: null,
        });
      }, STRIKE_MOVE_DELAY_MS);

      const name =
        skills.find((x) => x.requirement_skill_id === id)?.name || 'Skill';
      toast({
        message: `Moved “${name}” to skills you don’t have`,
        duration: 5000,
        action: (
          <ToastAction altText="Undo" onClick={() => setAnswer(id, 'yes')}>
            Undo
          </ToastAction>
        ),
      });
      return;
    }

    setRow(id, { answer, striking: false, confirmed: false, expanded: false });
  };

  // Mobile tap-to-expand only previews the fields — it does not itself mean
  // "Yes" (that stays an explicit choice inside the panel), so a curious tap
  // can't silently commit an answer.
  const toggleExpand = (id) =>
    setRow(id, {
      expanded: !skills.find((x) => x.requirement_skill_id === id)?.expanded,
    });

  const setField = (id, key, value) => setRow(id, { [key]: value });

  const handleSaveRow = (id) => {
    const s = skills.find((x) => x.requirement_skill_id === id);
    if (!s) return;
    const miss = rowMissing({ ...s, answer: 'yes' });
    if (miss.length) {
      setRow(id, { error: true });
      toast({
        message: 'Add the highlighted details to save this skill.',
        variant: 'destructive',
      });
      return;
    }
    trackEvent('skill_saved', 'Senior Flow Skills', s.name);
    setRow(id, { answer: 'yes', confirmed: true, expanded: false });
  };

  const handleExpandConfirmed = (id) =>
    setRow(id, { confirmed: false, expanded: true });

  // Mobile swipe-to-reject. Drag is applied imperatively via refs (no
  // re-render per pixel); only the commit/snap-back decision touches React
  // state. `touch-action: pan-y` on the swipeable element (CSS) lets vertical
  // scroll pass through natively while this handles horizontal drags.
  const dragRefs = useRef({});
  const bindSwipeRefs = (id) => ({
    contentRef: (el) => {
      dragRefs.current[id] = { ...dragRefs.current[id], contentEl: el };
    },
    revealRef: (el) => {
      dragRefs.current[id] = { ...dragRefs.current[id], revealEl: el };
    },
  });

  const handleSwipeStart = (id, e) => {
    const t = e.touches[0];
    dragRefs.current[id] = {
      ...dragRefs.current[id],
      startX: t.clientX,
      startY: t.clientY,
      dx: 0,
      intent: null,
    };
  };

  const handleSwipeMove = (id, e) => {
    const st = dragRefs.current[id];
    if (!st) return;
    const t = e.touches[0];
    const dx = t.clientX - st.startX;
    const dy = t.clientY - st.startY;
    if (!st.intent) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      st.intent = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
    }
    if (st.intent !== 'h') return;
    const clamped = Math.max(-SWIPE_MAX_PX, Math.min(0, dx));
    st.dx = clamped;
    if (st.contentEl) st.contentEl.style.transform = `translateX(${clamped}px)`;
    if (st.revealEl) {
      st.revealEl.style.opacity = String(
        Math.min(1, Math.abs(clamped) / SWIPE_COMMIT_PX)
      );
    }
  };

  const handleSwipeEnd = (id) => {
    const st = dragRefs.current[id];
    if (!st) return;
    const committed = st.intent === 'h' && Math.abs(st.dx) >= SWIPE_COMMIT_PX;
    if (st.contentEl) {
      st.contentEl.style.transition =
        'transform 0.22s var(--ease-out, cubic-bezier(0.16, 1, 0.3, 1))';
      st.contentEl.style.transform = 'translateX(0)';
      window.setTimeout(() => {
        if (st.contentEl) st.contentEl.style.transition = '';
      }, 240);
    }
    if (st.revealEl) st.revealEl.style.opacity = '0';
    dragRefs.current[id] = {
      contentEl: st.contentEl,
      revealEl: st.revealEl,
    };
    if (committed) setAnswer(id, 'no');
  };

  const answeredCount = skills.filter((s) => s.answer != null).length;
  const pct = skills.length
    ? Math.round((answeredCount / skills.length) * 100)
    : 0;

  const primary = useMemo(
    () => skills.filter((s) => s.answer !== 'no'),
    [skills]
  );
  const notApplicable = useMemo(
    () => skills.filter((s) => s.answer === 'no'),
    [skills]
  );

  const handleSubmit = () => {
    if (hasScope && (isLoading || isError)) return;

    let firstBadId = null;
    const next = skills.map((s) => {
      const bad = s.answer == null || (s.answer === 'yes' && !s.confirmed);
      if (bad && firstBadId == null) firstBadId = s.requirement_skill_id;
      return { ...s, error: s.answer === 'yes' ? s.error : bad };
    });
    setSkills(next);

    if (firstBadId != null) {
      toast({
        message: 'A few skills still need an answer saved.',
        variant: 'destructive',
      });
      window.setTimeout(() => {
        document
          .querySelector(`.slf-skill-row[data-id="${firstBadId}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return;
    }

    trackEvent('form_submit', 'Senior Flow', 'skills');

    // No token/secMappingId at all (e.g. the local dev preview) — nothing to
    // PATCH against, so just advance.
    if (!hasScope) {
      onSubmitted();
      return;
    }

    updateSkillMatch.mutate(toSkillMatchPayload(next), {
      onSuccess: () => {
        trackEvent('form_success', 'Senior Flow', 'skills');
        onSubmitted();
      },
      onError: () => {
        trackEvent('form_error', 'Senior Flow', 'skills');
        toast({
          message: "We couldn't save that just now. Please try again.",
          variant: 'destructive',
        });
      },
    });
  };

  const renderRow = (s) => {
    const id = s.requirement_skill_id;
    const starVal = SK_LEVEL_TO_STAR[s.proficiency] || 0;
    const miss = s.error ? rowMissing({ ...s, answer: 'yes' }) : [];
    const isStriking = !!s.striking;
    const isConfirmed = !isStriking && s.answer === 'yes' && s.confirmed;
    // Desktop: the panel opens once "Yes" is picked. Mobile: tapping the
    // row just previews the panel (`expanded`) without committing an answer
    // — the explicit "Yes, I have this" inside is still what sets `answer`.
    const isOpen =
      !isStriking &&
      !s.confirmed &&
      s.answer !== 'no' &&
      (isMobile ? s.expanded : s.answer === 'yes');
    const noActive = isStriking || s.answer === 'no';
    const yesActive = !isStriking && s.answer === 'yes';

    // Collapsed confirmed row — same click-to-reopen rowlet pattern as
    // SkillCard.jsx's `collapsedConfirmed` branch.
    if (isConfirmed) {
      const meta = buildRowMeta(s);
      return (
        <li
          key={s.requirement_skill_id}
          className="slf-skill-row slf-skill-rowlet is-confirmed"
          data-id={s.requirement_skill_id}
        >
          <button
            type="button"
            className="slf-skill-rowlet-btn"
            onClick={() => handleExpandConfirmed(s.requirement_skill_id)}
            aria-expanded="false"
            aria-label={`${s.name}, confirmed — tap to edit`}
          >
            <span className="slf-skill-rowlet-check">
              <Check strokeWidth={2.5} aria-hidden="true" />
            </span>
            <span className="slf-skill-rowlet-text">
              <span className="slf-skill-name">{s.name}</span>
              {meta.length > 0 && (
                <span className="slf-skill-rl-meta">{meta}</span>
              )}
            </span>
            <ChevronRight
              className="slf-skill-rowlet-chevron"
              aria-hidden="true"
            />
          </button>
        </li>
      );
    }

    const headInner = <span className="slf-skill-name">{s.name}</span>;

    const rowClassName = `slf-skill-row${s.answer === 'no' ? ' is-na' : ''}${
      isStriking ? ' is-striking' : ''
    }${s.error ? ' is-error' : ''}${isOpen ? ' is-active' : ''}`;

    const body = (
      <>
        <div className="slf-skill-trig">
          {isMobile ? (
            <button
              type="button"
              className="slf-skill-head slf-skill-head--btn"
              onClick={() => toggleExpand(id)}
              aria-expanded={isOpen}
            >
              {headInner}
            </button>
          ) : (
            <div className="slf-skill-head">{headInner}</div>
          )}

          {isMobile ? (
            <div className="slf-skill-mobile-actions">
              {/* Once marked "No", this doubles as the un-reject control —
                  the toast's Undo only lasts a few seconds, so there needs
                  to be a lasting way back without the Yes/No pills. */}
              <button
                type="button"
                className={`slf-skill-mobile-no${noActive ? ' is-active' : ''}`}
                onClick={() => setAnswer(id, noActive ? null : 'no')}
                aria-label={
                  noActive
                    ? `Undo — ${s.name} is marked as a skill you don't have`
                    : `Mark ${s.name} as a skill you don't have`
                }
              >
                <X aria-hidden="true" />
              </button>
              {!noActive && (
                <ChevronDown
                  className={`slf-skill-chevron${isOpen ? ' is-open' : ''}`}
                  aria-hidden="true"
                />
              )}
            </div>
          ) : (
            <div
              className="slf-skill-ask-btns"
              role="group"
              aria-label={`Do you have ${s.name}?`}
            >
              <button
                type="button"
                className={`slf-skill-ask-btn slf-skill-ask-btn--yes${
                  yesActive ? ' is-active' : ''
                }`}
                aria-pressed={yesActive}
                onClick={() => setAnswer(id, 'yes')}
              >
                <span className="slf-skill-ask-ic">
                  <Check aria-hidden="true" />
                </span>
                Yes
              </button>
              <button
                type="button"
                className={`slf-skill-ask-btn slf-skill-ask-btn--no${
                  noActive ? ' is-active' : ''
                }`}
                aria-pressed={noActive}
                onClick={() => setAnswer(id, 'no')}
              >
                <span className="slf-skill-ask-ic">
                  <X aria-hidden="true" />
                </span>
                No
              </button>
            </div>
          )}
        </div>

        {isOpen && (
          <div className="slf-skill-panel">
            <div className="slf-skill-duo">
              <div
                className={`slf-skill-field${
                  miss.includes('experience') ? ' is-error' : ''
                }`}
              >
                <span className="slf-skill-field-label">
                  Experience
                  {miss.includes('experience') && (
                    <span className="slf-skill-req slf-skill-req--error">
                      Required
                    </span>
                  )}
                </span>
                <div className="slf-input-group">
                  <input
                    type="number"
                    min="0"
                    max="600"
                    inputMode="numeric"
                    placeholder="0"
                    aria-label={`Months of experience for ${s.name}`}
                    value={s.months}
                    onChange={(e) =>
                      setField(s.requirement_skill_id, 'months', e.target.value)
                    }
                  />
                  <span className="slf-sfx">months</span>
                </div>
              </div>

              <div
                className={`slf-skill-field${
                  miss.includes('proficiency') ? ' is-error' : ''
                }`}
              >
                <span className="slf-skill-field-label">
                  Proficiency
                  {miss.includes('proficiency') && (
                    <span className="slf-skill-req slf-skill-req--error">
                      Required
                    </span>
                  )}
                </span>
                <div className="slf-skill-stars-row">
                  <div
                    className="slf-skill-stars"
                    role="radiogroup"
                    aria-label={`Proficiency for ${s.name}`}
                  >
                    {[4, 3, 2, 1].map((si) => (
                      <button
                        key={si}
                        type="button"
                        className={`slf-skill-star${starVal >= si ? ' is-on' : ''}`}
                        role="radio"
                        aria-checked={starVal >= si}
                        aria-label={`${si} ${si === 1 ? 'star' : 'stars'} – ${skProfLabel(
                          SK_STAR_TO_LEVEL[si]
                        )}`}
                        onClick={() =>
                          setField(
                            s.requirement_skill_id,
                            'proficiency',
                            SK_STAR_TO_LEVEL[si]
                          )
                        }
                      >
                        <Star aria-hidden="true" />
                      </button>
                    ))}
                  </div>
                  <span
                    className={`slf-skill-stars-caption${starVal ? '' : ' is-empty'}`}
                  >
                    {starVal
                      ? skProfLabel(s.proficiency)
                      : 'Tap a star to rate'}
                  </span>
                </div>
              </div>
            </div>

            <div
              className={`slf-skill-field${
                miss.includes('recency') ? ' is-error' : ''
              }`}
            >
              <span className="slf-skill-field-label">
                Last used
                {miss.includes('recency') && (
                  <span className="slf-skill-req slf-skill-req--error">
                    Required
                  </span>
                )}
              </span>
              <div
                className="slf-chips"
                role="group"
                aria-label={`Last used: ${s.name}`}
              >
                {SK_RECENCY.map((r) => (
                  <button
                    key={r.key}
                    type="button"
                    className={`slf-skill-pill${
                      s.recencyBand === r.key ? ' selected' : ''
                    }`}
                    aria-pressed={s.recencyBand === r.key}
                    onClick={() =>
                      setField(s.requirement_skill_id, 'recencyBand', r.key)
                    }
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="slf-skill-panel-actions">
              <button
                type="button"
                className="slf-btn slf-btn-primary slf-skill-save"
                onClick={() => handleSaveRow(id)}
              >
                <Check aria-hidden="true" />
                {isMobile ? 'Yes, save this' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </>
    );

    if (isMobile) {
      const { contentRef, revealRef } = bindSwipeRefs(id);
      return (
        <li key={id} className={rowClassName} data-id={id}>
          <div
            className="slf-skill-swipe-reveal"
            ref={revealRef}
            aria-hidden="true"
          >
            <X aria-hidden="true" />
            <span>Skills you don&apos;t have</span>
          </div>
          <div
            className="slf-skill-swipe-content"
            ref={contentRef}
            onTouchStart={(e) => handleSwipeStart(id, e)}
            onTouchMove={(e) => handleSwipeMove(id, e)}
            onTouchEnd={() => handleSwipeEnd(id)}
            onTouchCancel={() => handleSwipeEnd(id)}
          >
            {body}
          </div>
        </li>
      );
    }

    return (
      <li key={id} className={rowClassName} data-id={id}>
        {body}
      </li>
    );
  };

  let listBody;
  if (!hasScope) {
    listBody = (
      <div className="slf-banner slf-info" style={{ marginTop: 16 }}>
        <AlertCircle className="slf-ic" aria-hidden="true" />
        <div>
          Skill confirmation isn&apos;t available right now — you can continue
          without it.
        </div>
      </div>
    );
  } else if (isLoading) {
    listBody = (
      <p className="slf-read" style={{ marginTop: 16 }}>
        Loading your skills…
      </p>
    );
  } else if (isError) {
    listBody = (
      <div className="slf-banner slf-warn" style={{ marginTop: 16 }}>
        <AlertCircle className="slf-ic" aria-hidden="true" />
        <div>
          We couldn&apos;t load your skills.{' '}
          <button
            type="button"
            className="slf-btn-link"
            onClick={() => refetch()}
          >
            Try again
          </button>
        </div>
      </div>
    );
  } else if (skills.length === 0) {
    listBody = (
      <p className="slf-read" style={{ marginTop: 16 }}>
        No specific skills to confirm for this role.
      </p>
    );
  } else {
    listBody = (
      <>
        <div className="slf-skillbar" role="status" aria-live="polite">
          <div
            className="slf-skillbar-track"
            role="progressbar"
            aria-valuenow={answeredCount}
            aria-valuemin={0}
            aria-valuemax={skills.length}
            aria-label="Skills reviewed"
          >
            <span style={{ width: `${pct}%` }} />
          </div>
          <span className="slf-skillbar-text">
            <b>{answeredCount}</b> of <b>{skills.length}</b> reviewed
          </span>
        </div>

        <hr className="slf-rule" style={{ margin: '18px 0' }} />

        <ul className="slf-skill-list">{primary.map(renderRow)}</ul>

        {notApplicable.length > 0 && (
          <div className="slf-skill-napile">
            <div className="slf-skill-napile-head">
              <AlertCircle aria-hidden="true" />
              <span>Skills you don&apos;t have</span>
              <span className="slf-skill-napile-count">
                {notApplicable.length}
              </span>
            </div>
            <ul className="slf-skill-list">{notApplicable.map(renderRow)}</ul>
          </div>
        )}
      </>
    );
  }

  return (
    <section className="slf-stage-in" aria-labelledby="slf-skillsHead">
      <p className="slf-eyebrow">Last step · about 2 minutes</p>
      <h2 id="slf-skillsHead" style={{ marginTop: 10 }}>
        Do you have these skills, {firstName}?
      </h2>
      <p className="slf-read" style={{ marginTop: 8 }}>
        {companyName} is specifically looking for these. Tell us which ones you
        have — it&apos;s how we match you accurately against the role.
      </p>

      {listBody}

      <div className="slf-commit" style={{ marginTop: 20 }}>
        <div className="slf-actions">
          <button
            className="slf-btn slf-btn-primary"
            type="button"
            onClick={handleSubmit}
            disabled={
              (hasScope && (isLoading || isError)) || updateSkillMatch.isPending
            }
          >
            {updateSkillMatch.isPending ? 'Saving…' : 'Save and continue'}
          </button>
          <button
            className="slf-btn slf-btn-secondary"
            type="button"
            onClick={onBack}
          >
            Back
          </button>
        </div>
      </div>
    </section>
  );
}

SkillsStage.propTypes = {
  scope: PropTypes.shape({
    token: PropTypes.string,
    secMappingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  // sec_mapping_id off the already-fetched landing response — preferred over
  // decoding it back out of the token for the skill-match calls.
  landingSecMappingId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  firstName: PropTypes.string,
  companyName: PropTypes.string,
  onSubmitted: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

SkillsStage.defaultProps = {
  scope: {},
  landingSecMappingId: null,
  firstName: 'there',
  companyName: 'the company',
};

export default SkillsStage;
